import { useEffect, useRef } from "react";
import Sidebar from "./Sidebar";
import GlobalTopBar from "./GlobalTopBar";
import ModuleTabs from "./ModuleTabs";
import { Outlet, useLocation } from "react-router-dom";
import { SidebarProvider, useSidebar } from "@/context/SidebarContext";

/** Guarda y restaura la posición de scroll del contenedor principal por
 * ruta usando sessionStorage. Cuando el usuario cambia entre solapas del
 * ModuleTabs, cada solapa recupera exactamente el scroll donde había
 * quedado (aunque el componente se remontó). Se guarda:
 *   - Al desmontarse el efecto anterior (justo antes de un cambio de ruta).
 *   - Cada 400ms mientras hace scroll (por si recarga el navegador).
 */
function useTabScrollMemory(scrollRef) {
  const location = useLocation();
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const path = location.pathname + location.search;
    const key = `labiferia:scroll:${path}`;
    // Restaurar
    const raw = sessionStorage.getItem(key);
    const saved = raw ? parseInt(raw, 10) : 0;
    // Doble raf: espera a que Outlet monte y layout se estabilice.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (el && Number.isFinite(saved)) el.scrollTop = saved;
      });
    });
    // Guardar mientras se scrollea (throttled a 400 ms)
    let t = null;
    const onScroll = () => {
      if (t) return;
      t = setTimeout(() => {
        try { sessionStorage.setItem(key, String(el.scrollTop)); } catch { /* noop */ }
        t = null;
      }, 400);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      // Guardar posición actual al salir de esta ruta.
      try { sessionStorage.setItem(key, String(el.scrollTop)); } catch { /* noop */ }
      el.removeEventListener("scroll", onScroll);
      if (t) clearTimeout(t);
    };
  }, [location.pathname, location.search, scrollRef]);
}

function LayoutInner() {
  const { pinned, visible, show, hideSoon } = useSidebar();
  const scrollRef = useRef(null);
  useTabScrollMemory(scrollRef);
  return (
    <div className="flex h-screen overflow-hidden bg-[#F9F9F8]">
      {pinned ? (
        <Sidebar />
      ) : (
        <>
          {/* Zona caliente en el borde izquierdo: abre el panel al apoyar el mouse */}
          <div
            className="fixed left-0 top-0 h-screen w-2 z-40"
            onMouseEnter={show}
            onMouseLeave={hideSoon}
            data-testid="sidebar-hotzone"
          />
          <div
            className={`fixed left-0 top-0 h-screen z-50 transition-transform duration-200 ease-out shadow-2xl ${visible ? "translate-x-0" : "-translate-x-full"}`}
            onMouseEnter={show}
            onMouseLeave={hideSoon}
            data-testid="sidebar-floating"
          >
            <Sidebar />
          </div>
        </>
      )}
      <main className="flex-1 min-w-0 max-w-full flex flex-col overflow-hidden">
        <GlobalTopBar />
        <ModuleTabs />
        <div
          ref={scrollRef}
          className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden"
          data-testid="app-scroll-container"
        >
          <Outlet />
        </div>
      </main>
    </div>
  );
}

export default function AppLayout() {
  return (
    <SidebarProvider>
      <LayoutInner />
    </SidebarProvider>
  );
}
